import { toast } from "react-toastify";
import { currentFeatures, endpoints, oids } from "../../../../configs/Settings";
import { HTTPResponseStatus } from "../../../../utils/enum";
import { useAuth } from "../../../../utils/useAuth";
import { useAxios } from "../../../../utils/useAxios";

interface formatFields {
  patient: number | string;
  questionnaire?: string;
}

interface GetQuestionnairesResponses {
  patient: any;
  questionnaire?: string;
  count?: number;
  status?: string;
  tag?: string | string[];
  sort?: string;
  author?: string;
  subject?: string;
  organization?: string;
}

export async function GetQuestionnaireResponse({
  patient,
  questionnaire,
}: formatFields) {
  const patientId = currentFeatures.defaultApp
    ? `${oids.cpf}-${patient}`
    : patient;
  const url = `${endpoints.MHD}/QuestionnaireResponse?subject=Patient/${patientId}&questionnaire=${questionnaire}&_sort=-_lastUpdated&_count=250`;
  const subjectId = await useAuth.getSubjectId();
  const organizationId = await useAuth.getOrganizationId();
  const headers = {
    Accept: "application/fhir+json",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
    ...(subjectId && { "subject-id": subjectId }),
    ...(organizationId && { "organization-id": organizationId }),
  };
  const response = await useAxios
    .get(url, { headers, timeout: 20000 })
    .then((response) => {
      if (response.status !== HTTPResponseStatus.Success) {
        throw new Error(`Get QuestionnaireResponse: ${response.status}`);
      }
      return response.data;
    })
    .catch((error) => {
      console.error("GetQuestionnaire ", error);
      // toast.error(`GetQuestionnaire Error: ${error?.response?.status}`);
      throw new Error();
    });
  if (response) return response;
}

export async function GetQuestionnairesResponse({ patient }: { patient: any }) {
  const url = `${endpoints.MHD}/QuestionnaireResponse?subject=Patient/${oids.cpf}-${patient}`;
  const subjectId = await useAuth.getSubjectId();
  const organizationId = await useAuth.getOrganizationId();
  const headers = {
    Accept: "application/fhir+json",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
    // "x-user-token": await useAuth.getXUserToken(),
    // "organization-id": "05490544000100",
    ...(subjectId && { "subject-id": subjectId }),
    ...(organizationId && { "organization-id": organizationId }),
  };
  const response = await useAxios
    .get(url, { headers, timeout: 20000 })
    .then((response) => {
      if (response.status !== HTTPResponseStatus.Success) {
        throw new Error(`Get QuestionnaireResponse: ${response.status}`);
      }
      return response.data;
    })
    .catch((error) => {
      console.error("GetQuestionnaire ", error);
      // toast.error(`GetQuestionnaire Error: ${error?.response?.status}`);
      throw new Error();
    });
  if (response) return response;
}

export async function GetQuestionnairesResponses({
  patient,
  tag = ["questionnaires"],
  count = 250,
  sort = "-_lastUpdated",
  author,
  subject,
  organization,
  questionnaire,
  status,
}: GetQuestionnairesResponses) {
  const queryParams: string[] = [];
  if (count) queryParams.push(`_count=${count}`);

  if (tag) {
    if (Array.isArray(tag)) {
      tag.forEach((t) => queryParams.push(`_tag=${encodeURIComponent(t)}`));
    } else {
      queryParams.push(`_tag=${encodeURIComponent(tag)}`);
    }
  }

  if (sort) queryParams.push(`_sort=${sort}`);
  if (author) queryParams.push(`author=${encodeURIComponent(author)}`);
  if (subject) queryParams.push(`subject=${encodeURIComponent(subject)}`);
  if (organization)
    queryParams.push(`organization=${encodeURIComponent(organization)}`);
  if (questionnaire)
    queryParams.push(`questionnaire=${encodeURIComponent(questionnaire)}`);
  if (status) queryParams.push(`status=${encodeURIComponent(status)}`);

  const url = `${endpoints.MHD}/QuestionnaireResponse?subject=Patient/${
    oids.cpf
  }-${patient}${queryParams.length > 0 ? `&${queryParams.join("&")}` : ""}`;
  const subjectId = await useAuth.getSubjectId();
  const organizationId = await useAuth.getOrganizationId();
  const headers = {
    Accept: "application/fhir+json",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
    ...(subjectId && { "subject-id": subjectId }),
    ...(organizationId && { "organization-id": organizationId }),
  };
  const response = await useAxios
    .get(url, { headers, timeout: 20000 })
    .then((response) => {
      if (response.status !== HTTPResponseStatus.Success) {
        throw new Error(`Get QuestionnaireResponse: ${response.status}`);
      }
      return response.data;
    })
    .catch((error) => {
      console.error("GetQuestionnairesResponses ", error);
      // toast.error(`GetQuestionnairesResponses Error: ${error?.response?.status}`);
      throw new Error();
    });
  if (response) return response;
}

export async function GetQuestionnaireResponsesByPractitioner({
  practitioner,
}: any) {
  const url = `${endpoints.MHD}/QuestionnaireResponse?author=Practitioner/${oids.cpf}-${practitioner}&subject:Patient.identifier=urn:oid:${oids.cpf}%7C${practitioner}`;
  const subjectId = await useAuth.getSubjectId();
  const organizationId = await useAuth.getOrganizationId();
  const headers = {
    Accept: "application/fhir+json",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
    // "x-user-token": await useAuth.getXUserToken(),
    ...(subjectId && { "subject-id": subjectId }),
    ...(organizationId && { "organization-id": organizationId }),
  };
  const response = await useAxios
    .get(url, { headers, timeout: 20000 })
    .then((response) => {
      if (response.status !== HTTPResponseStatus.Success) {
        throw new Error(`Get QuestionnaireResponse: ${response.status}`);
      }
      return response.data;
    })
    .catch((error) => {
      console.error("GetQuestionnaireResponsesByPractitioner ", error);
      // toast.error(
      //   `GetQuestionnaireResponsesByPractitioner Error: ${error?.response?.status}`
      // );
      throw new Error();
    });
  if (response) return response;
}

/**
 * Obtém um QuestionnaireResponse específico pelo seu ID
 * @param id ID do QuestionnaireResponse que deseja buscar
 * @param patient CPF do paciente para filtrar o QuestionnaireResponse
 * @returns O QuestionnaireResponse obtido
 */
export async function GetQuestionnaireResponseById(
  id: string,
  patient: string
) {
  const url = `${endpoints.MHD}/QuestionnaireResponse?_id=${id}&subject=Patient/${oids.cpf}-${patient}&_sort=-_lastUpdated`;

  const subjectId = await useAuth.getSubjectId();
  const organizationId = await useAuth.getOrganizationId();

  const headers = {
    Accept: "application/fhir+json",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
    ...(subjectId && { "subject-id": subjectId }),
    ...(organizationId && { "organization-id": organizationId }),
  };

  const response = await useAxios
    .get(url, { headers, timeout: 20000 })
    .then((response) => {
      if (response.status !== HTTPResponseStatus.Success) {
        throw new Error(`Get QuestionnaireResponse by ID: ${response.status}`);
      }
      return response.data;
    })
    .catch((error) => {
      console.error("GetQuestionnaireResponseById ", error);
      // toast.error(`GetQuestionnaireResponseById Error: ${error?.response?.status}`);
      throw new Error();
    });

  if (response) return response;
}
